import useSendTransactionContext from '@hooks/useSendTransactionContext';
import useWallets from '@hooks/useWallets';
import { getChainDisplayName, shortenAddress } from '@lib/utils';
import React, { useCallback } from 'react';
import MinifiedBridgeTransactionListItem from './MinifiedBridgeTransactionListItem';
import Notification from './Notification';
import WalletIcon from './WalletIcon';
import MobileMenu from './MobileMenu';
import useHasConnectedWallet from '@hooks/useHasConnectedWallet';
import { useRouter } from 'next/router';

const WalletsMobileMenu = ({
  onMenuItemClick,
}: {
  onMenuItemClick: () => void;
}) => {
  const router = useRouter();
  const { wallets, disconnect } = useWallets();
  const getIsConnected = useHasConnectedWallet();
  const { bridgeTxNotifications, hideBridgeTxNotification } =
    useSendTransactionContext();

  const onHistoryButtonClick = useCallback(() => {
    router.push('/history');
    onMenuItemClick();
  }, [router, onMenuItemClick]);

  const onDisconnectButtonClick = useCallback(() => {
    const [firstWallet] = wallets;
    if (firstWallet) disconnect(firstWallet);
    onMenuItemClick();
  }, [wallets, disconnect, onMenuItemClick]);

  const renderWallets = () =>
    wallets
      .filter(({ active }) => active)
      .map((wallet) => (
        <MobileMenu.Item key={wallet.type} className="flex items-center">
          <WalletIcon
            variant={wallet.type}
            className="text-iconMedium"
            idPrefix="mobile-menu"
          />
          <span className="text-white text-smallRegular font-normal mx-1 flex-1">
            {shortenAddress(wallet.address)}
          </span>
          <span className="flex items-center bg-white text-black text-micro font-normal px-1.5 h-[22px] rounded-full">
            {getChainDisplayName(wallet.chain)}
          </span>
        </MobileMenu.Item>
      ));

  const renderBridgeTxNotifications = () =>
    bridgeTxNotifications
      .filter(({ isHidden }) => !isHidden)
      .map(({ bridgeTx: { id, depositTx, transferTx } }, index) => {
        return (
          <Notification key={depositTx.hash}>
            <MinifiedBridgeTransactionListItem
              depositTxnHash={depositTx.hash || ''}
              transferTxnHash={transferTx.hash || ''}
              depositStatus={depositTx.status}
              transferStatus={transferTx.status}
              network={depositTx.network}
              index={index}
              onCloseNotification={() => hideBridgeTxNotification(id)}
              chain={depositTx.chain}
            />
          </Notification>
        );
      });

  return (
    <MobileMenu.Container>
      {renderWallets()}
      {renderBridgeTxNotifications()}
      {getIsConnected(wallets) && (
        <MobileMenu.Item onClick={onHistoryButtonClick}>
          History
        </MobileMenu.Item>
      )}
      <MobileMenu.Item onClick={onDisconnectButtonClick}>
        Disconnect
      </MobileMenu.Item>
    </MobileMenu.Container>
  );
};

export default WalletsMobileMenu;
